import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Compass, Loader2, AlertTriangle, RefreshCw, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { KbEditor } from './components/KbEditor';
import {
  NavExceptionsDashboard, CaseForms, TracksPanel, AttachmentsPanel, ExceptionRow,
} from './components/NavExceptionsDashboard';
import { NavBulkReassignTab } from './components/NavBulkReassignTab';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  adminNavApi, ADMIN_NAV_KEYS, downloadAdminNavCsv,
  type AdminNavFilters, type AdminNavSummary, type AdminNavByNavigator,
  type AdminNavExceptionItem, type AdminNavLoad,
  type PriorityListPreview, type PriorityListApplyResult,
} from '@/features/navigator/adminNavigation.api';
import { KB_KEYS } from '@/features/navigator/kb.api';
import { navError, type ClosedReasonCode, type NavStatus } from '@/features/navigator/navigator.api';
import { toast } from '@/stores/toast.store';
import { useAuthStore } from '@/stores/auth.store';
import { useT, type StringKey } from '@/i18n';
import { cn } from '@/lib/utils';
import { Pager as ChassisPager } from '@/components/chassis/Pager';
import { STATUS_KEYS, FINDHELP_HISTORY_KEYS, CLOSED_REASON_KEYS } from '../navigator/labels';

const fhLabel = (k: string): StringKey => FINDHELP_HISTORY_KEYS[k] ?? 'adminNav.fh.none';

const POLL_MS = 30_000;
type Tab = 'exceptions' | 'summary' | 'byNavigator' | 'cases' | 'priorityList' | 'reassign' | 'kb' | 'config';
const TABS_SET = new Set<Tab>(['exceptions', 'summary', 'byNavigator', 'cases', 'priorityList', 'reassign', 'kb', 'config']);

/**
 * F5-R4a — el período global de la cabecera (dibujo del CEO: «manda sobre
 * todas las pestañas»). Los TRES valores vivos son los del endpoint
 * by-navigator (today/week/month); «30 días» y «Personalizado» del dibujo no
 * existen server-side y se señalizan deshabilitados (NECESITA-SERVER).
 * «Hoy» no está en el dibujo pero es funcionalidad viva del window select que
 * este control reemplaza — conserva (precedente E-23).
 */
export type NavAdminPeriod = 'today' | 'week' | 'month';

const openSum = (s: AdminNavSummary): number =>
  (s.byStatus.suggested ?? 0) + (s.byStatus.flagged ?? 0) + (s.byStatus.in_progress ?? 0);
const closedSum = (s: AdminNavSummary): number =>
  (s.byStatus.completed ?? 0) + (s.byStatus.declined_by_patient ?? 0)
  + (s.byStatus.not_required ?? 0) + (s.byStatus.closed_external ?? 0);

/**
 * /admin/navigation — the module's control room (design §9-bis).
 *
 * F5-R4a: estructura del dibujo del CEO «Navegación v2 — Centro de control»
 * (2026-08-28). Tarjeta con cabecera blanca pegada al tope, período global,
 * botón Actualizar y UNA fila de pestañas con subrayado teal y contador donde
 * hay trabajo. Exceptions (money at risk) sigue siendo la landing.
 */
export function NavigationAdminPage(): JSX.Element {
  const t = useT();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab: Tab = TABS_SET.has(requested as Tab) ? (requested as Tab) : 'exceptions';
  const setTab = (next: Tab): void => {
    const nextParams = new URLSearchParams(params);
    if (next === 'exceptions') nextParams.delete('tab');
    else nextParams.set('tab', next);
    setParams(nextParams, { replace: true });
  };

  const [period, setPeriod] = useState<NavAdminPeriod>('today');

  // Los contadores de la barra (dibujo: «la barra dice dónde hay trabajo»).
  // Tres endpoints vivos y sin PHI; las mismas keys que usan las pestañas, así
  // que react-query los dedupe con las queries internas de cada tab.
  const exceptionsQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.exceptions,
    queryFn: () => adminNavApi.exceptions(),
    refetchInterval: POLL_MS,
  });
  const summaryQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.summary,
    queryFn: () => adminNavApi.summary(),
    refetchInterval: POLL_MS,
  });
  const poolBadgeQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.bulkPool('in_pool'),
    queryFn: () => adminNavApi.bulkReassignPoolCount('in_pool'),
    refetchInterval: POLL_MS,
  });

  const excCount = exceptionsQ.data?.items.length ?? null;
  const casesCount = summaryQ.data ? openSum(summaryQ.data) : null;
  const reassignCount = poolBadgeQ.data?.poolCount ?? null;

  const TABS: Array<{ key: Tab; label: string; count: number | null; amber?: boolean }> = [
    { key: 'exceptions', label: t('admin.nav.tab.exceptions'), count: excCount, amber: true },
    { key: 'summary', label: t('adminNav.tab.summary'), count: null },
    { key: 'byNavigator', label: t('adminNav.tab.byNavigator'), count: null },
    { key: 'cases', label: t('adminNav.tab.cases'), count: casesCount },
    { key: 'priorityList', label: t('adminNav.tab.priorityList'), count: null },
    { key: 'reassign', label: t('adminNav.tab.reassign'), count: reassignCount },
    { key: 'kb', label: t('adminNav.tab.kb'), count: null },
    { key: 'config', label: t('adminNav.tab.config'), count: null },
  ];

  const PERIODS: Array<{ key: NavAdminPeriod | null; label: string; naTitle?: string }> = [
    { key: 'today', label: t('adminNav.window.today') }, // conserva (E-23): window vivo que el dibujo no dibuja
    { key: 'week', label: t('f5r4a.period.7d') },
    { key: null, label: t('f5r4a.period.30d'), naTitle: t('f5r4a.period.na') },
    { key: 'month', label: t('adminNav.window.month') },
    { key: null, label: t('f5r4a.period.custom'), naTitle: t('f5r4a.period.na') },
  ];
  const periodLabel = period === 'today'
    ? t('adminNav.window.today')
    : period === 'week' ? t('f5r4a.period.7d') : t('adminNav.window.month');

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['admin', 'navigation'] });
    void qc.invalidateQueries({ queryKey: KB_KEYS.admin });
  };

  return (
    <div className="flex flex-col gap-5 font-body text-nav-navy">
      <section className="rounded-[14px] border-[1.5px] border-nav-card bg-nav-soft overflow-hidden animate-fade-up-slow motion-reduce:animate-none">
        {/* Cabecera blanca pegada al tope de la tarjeta (dibujo: «Medidas y color»). */}
        <div className="bg-white border-b-[1.5px] border-nav-card">
          <div className="flex items-start justify-between gap-5 flex-wrap px-7 pt-[22px]">
            <div className="min-w-0">
              <div className="flex items-center gap-2.5 mb-1.5">
                <span className="flex-none inline-flex items-center justify-center w-[30px] h-[30px] rounded-full bg-nav-tealTint text-nav-tealDark">
                  <Compass className="h-[17px] w-[17px]" aria-hidden />
                </span>
                <h1 className="font-head text-[20px] font-extrabold tracking-[-0.02em] text-nav-navy">
                  {t('f5r4a.head.title')}
                </h1>
                {/* «el rango elegido queda escrito al lado del título» */}
                <span className="text-[12px] text-nav-slate font-head font-semibold mt-0.5">· {periodLabel}</span>
              </div>
              <p className="text-[13.5px] leading-normal text-nav-slate">{t('f5r4a.head.sub')}</p>
            </div>

            <div className="flex items-center gap-2.5 flex-wrap">
              <div
                className="inline-flex items-center gap-[2px] bg-white border-[1.5px] border-nav-card rounded-[10px] p-[3px]"
                role="group"
                aria-label={t('adminNav.window')}
                title={t('f5r4a.period.note')}
              >
                {PERIODS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    disabled={p.key === null}
                    aria-pressed={p.key !== null && period === p.key}
                    title={p.naTitle}
                    onClick={() => { if (p.key) setPeriod(p.key); }}
                    className={cn(
                      'min-h-[44px] px-[15px] rounded-lg font-head text-[12.5px] font-bold transition-colors duration-150 motion-reduce:transition-none',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
                      p.key === null
                        ? 'text-nav-faint cursor-not-allowed'
                        : period === p.key
                          ? 'bg-nav-navy text-white hover:bg-nav-navyHover'
                          : 'text-nav-slate hover:bg-nav-soft hover:text-nav-navy',
                    )}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={refresh}
                className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-lg border-[1.5px] border-nav-line bg-white font-head text-[13px] font-bold text-nav-navy hover:bg-nav-tealTint/40 hover:border-nav-teal transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
              >
                <RefreshCw className="h-[15px] w-[15px] flex-none" aria-hidden />
                {t('f5r4a.refresh')}
              </button>
            </div>
          </div>

          {/* Una fila de pestañas con subrayado teal 2px (280 ms) y contador. */}
          <div role="tablist" aria-label={t('f5r4a.head.title')}
               className="flex items-stretch gap-[2px] flex-wrap px-7 pt-[18px]">
            {TABS.map((x) => {
              const on = tab === x.key;
              const showCount = x.count !== null && x.count > 0;
              return (
                <button
                  key={x.key}
                  role="tab"
                  aria-selected={on}
                  aria-current={on ? 'page' : undefined}
                  aria-label={showCount ? `${x.label} (${x.count})` : undefined}
                  onClick={() => setTab(x.key)}
                  className="relative inline-flex items-center gap-2 flex-none min-h-[48px] px-3.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
                >
                  <span className={cn(
                    'font-head text-[13.5px] whitespace-nowrap transition-colors duration-150 motion-reduce:transition-none',
                    on ? 'font-bold text-nav-navy' : 'font-semibold text-nav-slate hover:text-nav-navy',
                  )}>
                    {x.label}
                  </span>
                  {showCount && (
                    <span className={cn(
                      'rounded-full px-2 py-0.5 border font-head text-[11px] font-bold tabular-nums',
                      x.amber
                        ? 'bg-nav-amberTint text-nav-amberText border-nav-amber/40'
                        : 'bg-nav-hair text-nav-slate border-nav-card',
                    )}>
                      {x.count}
                    </span>
                  )}
                  <span
                    aria-hidden
                    className={cn(
                      'absolute left-3.5 right-3.5 bottom-0 h-[2px] rounded bg-nav-teal origin-left transition-transform duration-[280ms] motion-reduce:transition-none',
                      on ? 'scale-x-100' : 'scale-x-0',
                    )}
                  />
                </button>
              );
            })}
          </div>
        </div>

        <div className="px-7 pt-[26px] pb-[30px]">
          {tab === 'exceptions' && <NavExceptionsDashboard />}
          {tab === 'summary' && <SummaryTab period={period} />}
          {tab === 'byNavigator' && <ByNavigatorTab period={period} />}
          {tab === 'cases' && <CasesTab />}
          {tab === 'priorityList' && <PriorityListTab />}
          {tab === 'reassign' && <NavBulkReassignTab />}
          {tab === 'kb' && <KbEditor />}
          {tab === 'config' && <ConfigTab />}
        </div>
      </section>
    </div>
  );
}

/**
 * F5-R4a — Resumen del dibujo: 6 tiles + buzón del pool + cierres por razón.
 * Cada número sale de un endpoint vivo y sin PHI (los conteos de list()
 * auditarían un NAV_QUEUE_VIEWED por poll — por eso Prioritarios y Buzón usan
 * pool-count, que existe para contar). NECESITA-SERVER: los deltas «vs.
 * período previo», la serie diaria del buzón y el corte por período de
 * cierres/Cerrados — señalizados en el copy, no fingidos.
 */
function SummaryTab({ period }: { period: NavAdminPeriod }): JSX.Element {
  const t = useT();
  const q = useQuery({
    queryKey: ADMIN_NAV_KEYS.summary,
    queryFn: () => adminNavApi.summary(),
    refetchInterval: POLL_MS,
  });
  const poolQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.bulkPool('in_pool'),
    queryFn: () => adminNavApi.bulkReassignPoolCount('in_pool'),
    refetchInterval: POLL_MS,
  });
  const somosQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.bulkPool('somos'),
    queryFn: () => adminNavApi.bulkReassignPoolCount('somos'),
    refetchInterval: POLL_MS,
  });
  const navsQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.bulkNavigators,
    queryFn: () => adminNavApi.bulkReassignNavigators(),
  });
  const byNavQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.byNavigator(period),
    queryFn: () => adminNavApi.byNavigator(period),
    refetchInterval: POLL_MS,
  });

  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <LoadError />;
  const s: AdminNavSummary = q.data;
  const open = openSum(s);
  const sessions = byNavQ.data
    ? byNavQ.data.rows.reduce((acc, r) => acc + (r.sessions ?? 0), 0)
    : null;

  const closures = Object.entries(s.byClosedReason ?? {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1]);
  const maxClosure = closures.length > 0 ? closures[0]?.[1] ?? 0 : 0;

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(168px, 1fr))' }}>
        <KpiTile value={open} label={t('f5r4a.sum.kpi.open')} />
        <KpiTile value={somosQ.data?.poolCount ?? null} label={t('f5r4a.sum.kpi.somos')} />
        <KpiTile value={poolQ.data?.poolCount ?? null} label={t('f5r4a.sum.kpi.pool')} />
        <KpiTile value={navsQ.data?.length ?? null} label={t('f5r4a.sum.kpi.navs')} />
        <KpiTile value={sessions} label={t('f5r4a.sum.kpi.sessions')} />
        <KpiTile value={closedSum(s)} label={t('f5r4a.sum.kpi.closed')} />
      </div>
      {/* NECESITA-SERVER: los «+8 vs. período previo» del dibujo. */}
      <p className="text-[11px] text-nav-slate -mt-2">{t('f5r4a.sum.deltaNa')}</p>

      <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <div className="rounded-xl border-[1.5px] border-nav-amber bg-nav-amberTint p-5">
          <p className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-amberText mb-3.5">
            {t('f5r4a.sum.poolKicker')}
          </p>
          <div className="flex items-end gap-2.5 mb-3">
            <span className="font-head text-[34px] font-extrabold leading-none tracking-[-0.02em] tabular-nums text-nav-amberText">
              {poolQ.data?.poolCount ?? '—'}
            </span>
            <span className="text-[12.5px] text-nav-amberText pb-1">{t('f5r4a.sum.poolUnit')}</span>
          </div>
          <p className="text-[12.5px] leading-relaxed text-nav-amberText">{t('f5r4a.sum.poolNote')}</p>
          {/* NECESITA-SERVER: la serie de 10 días y el chip «−14 esta semana». */}
          <p className="text-[11px] text-nav-amberText/80 mt-2">{t('f5r4a.sum.poolSeriesNa')}</p>
        </div>

        <div className="rounded-xl border-[1.5px] border-nav-card bg-white p-5">
          <p className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal mb-3.5">
            {t('f5r4a.sum.closuresKicker')}
          </p>
          {closures.length === 0 ? (
            <p className="text-sm text-nav-slate">{t('f5r4a.sum.closuresEmpty')}</p>
          ) : (
            <div className="flex flex-col gap-[11px]">
              {closures.map(([code, n]) => (
                <div key={code}>
                  <div className="flex items-baseline justify-between gap-3 mb-[5px]">
                    <span className="text-[13px] text-nav-navy">
                      {CLOSED_REASON_KEYS[code as ClosedReasonCode]
                        ? t(CLOSED_REASON_KEYS[code as ClosedReasonCode])
                        : code}
                    </span>
                    <span className="font-head text-[13px] font-bold tabular-nums text-nav-navy">{n}</span>
                  </div>
                  <div className="h-[6px] rounded-[3px] bg-nav-hair overflow-hidden">
                    <div
                      className="h-full rounded-[3px] bg-nav-teal transition-[width] duration-300 motion-reduce:transition-none"
                      style={{ width: `${maxClosure > 0 ? Math.round((n / maxClosure) * 100) : 0}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-nav-slate mt-3">{t('f5r4a.sum.closuresNa')}</p>
        </div>
      </div>

      {/* Conservas (E-23): las cifras vivas que el dibujo ni dibuja ni mata —
          urgentes, SLA, sin dueño, espejo pendiente y los tres paneles. */}
      <div className="flex gap-3 flex-wrap">
        <Stat value={s.unclaimed} label={t('adminNav.stat.unclaimed')} tone="peach" />
        <Stat value={s.urgent} label={t('adminNav.stat.urgent')} tone="rose" />
        <Stat value={s.slaOverdue} label={t('adminNav.stat.slaOverdue')} tone="rose" />
        <Stat value={s.slaDueSoon} label={t('adminNav.stat.slaDueSoon')} tone="peach" />
        <Stat value={s.pendingMirror.total} label={t('adminNav.stat.pendingMirror')} tone="peach" />
      </div>

      {s.awaitingFirstContact > 0 && (
        <Alert variant="info" role="status">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
          <AlertDescription>
            {t('adminNav.awaitingFirstContact', { n: s.awaitingFirstContact })}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Panel title={t('adminNav.byStatus')}>
          <dl className="text-sm">
            {Object.entries(s.byStatus).map(([k, v]) => (
              <div key={k} className="flex justify-between py-0.5">
                <dt className="text-nav-slate">{t(STATUS_KEYS[k as NavStatus] ?? 'adminNav.byStatus')}</dt>
                <dd className="font-semibold text-nav-navy">{v}</dd>
              </div>
            ))}
          </dl>
        </Panel>

        <Panel title={t('adminNav.byFindhelp')}>
          <dl className="text-sm">
            {(['none', 'incomplete', 'complete'] as const).map((k) => (
              <div key={k} className="flex justify-between py-0.5">
                <dt className="text-nav-slate">{t(fhLabel(k))}</dt>
                <dd className="font-semibold text-nav-navy">{s.byFindhelpHistory[k] ?? 0}</dd>
              </div>
            ))}
          </dl>
          <p className="text-[11px] text-nav-slate mt-2">{t('adminNav.byFindhelpHint')}</p>
        </Panel>

        <Panel title={t('adminNav.pendingMirrorTitle')}>
          <dl className="text-sm">
            {Object.entries(s.pendingMirror.parts).map(([k, v]) => (
              <div key={k} className="flex justify-between py-0.5">
                <dt className="text-nav-slate">{k}</dt>
                <dd className="font-semibold text-nav-navy">{v}</dd>
              </div>
            ))}
          </dl>
          <p className="text-[11px] text-nav-slate mt-2">
            {t('adminNav.notBuilt', { list: Object.entries(s.notBuiltYet).map(([k, w]) => `${k} (${w})`).join(' · ') })}
          </p>
        </Panel>
      </div>
    </div>
  );
}

function KpiTile({ value, label }: { value: number | null; label: string }): JSX.Element {
  return (
    <div className="rounded-xl border-[1.5px] border-nav-card bg-white px-5 pt-[18px] pb-4">
      <p className="font-head text-[30px] font-extrabold leading-none tracking-[-0.02em] tabular-nums text-nav-navy">
        {value ?? '—'}
      </p>
      <p className="text-[13px] text-nav-slate mt-[11px]">{label}</p>
    </div>
  );
}

/**
 * F5-R4a — Por navigator con las columnas del dibujo. Las «dos que conectan
 * la cartera con la plata»: Unidades ya venía del endpoint; Sin tracker se
 * agrega agregando las excepciones vivas por navigator (mismo número que la
 * compuerta del navigator, visto desde arriba); Vencidos sale de las cargas
 * de asignación. «Sin tocar ≥14 d» NECESITA-SERVER (señalizado con «—»).
 * La fila abre sus casos inline (excepciones vivas; el listado completo por
 * navigator también NECESITA-SERVER).
 */
function ByNavigatorTab({ period }: { period: NavAdminPeriod }): JSX.Element {
  const [drill, setDrill] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ADMIN_NAV_KEYS.byNavigator(period),
    queryFn: () => adminNavApi.byNavigator(period),
    refetchInterval: POLL_MS,
  });
  const loadsQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.loads,
    queryFn: () => adminNavApi.assignmentLoads(),
    refetchInterval: POLL_MS,
  });
  const excQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.exceptions,
    queryFn: () => adminNavApi.exceptions(),
    refetchInterval: POLL_MS,
  });
  const scopesQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.bulkNavigators,
    queryFn: () => adminNavApi.bulkReassignNavigators(),
  });

  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <LoadError />;
  return (
    <ByNavigatorTable
      data={q.data}
      overdueBy={new Map((loadsQ.data ?? []).map((l) => [String(l.navigatorId), l.overdueFollowups]))}
      gateBy={(excQ.data?.items ?? []).reduce((m, i) => {
        if (i.navigatorId) m.set(String(i.navigatorId), (m.get(String(i.navigatorId)) ?? 0) + i.unreimbursedSessions);
        return m;
      }, new Map<string, number>())}
      scopeBy={new Map((scopesQ.data ?? []).map((n) => [String(n.navigatorId), n.navigatorScope]))}
      drill={drill}
      setDrill={setDrill}
      excItems={excQ.data?.items ?? []}
      loads={loadsQ.data ?? []}
    />
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.charAt(0) ?? '' : '';
  return (first + last).toUpperCase();
}

function ChipCount({ n, red }: { n: number; red?: boolean }): JSX.Element {
  return (
    <span className={cn(
      'inline-flex rounded-full px-[11px] py-1 border font-head text-[12px] font-bold tabular-nums',
      n === 0
        ? 'bg-nav-hair text-nav-slate border-nav-card'
        : red
          ? 'bg-nav-redTint text-nav-red border-[#e3b5ae]'
          : 'bg-nav-amberTint text-nav-amberText border-nav-amber/40',
    )}>
      {n}
    </span>
  );
}

function ByNavigatorTable({
  data, overdueBy, gateBy, scopeBy, drill, setDrill, excItems, loads,
}: {
  data: AdminNavByNavigator;
  overdueBy: Map<string, number>;
  gateBy: Map<string, number>;
  scopeBy: Map<string, string>;
  drill: string | null;
  setDrill: (id: string | null) => void;
  excItems: AdminNavExceptionItem[];
  loads: AdminNavLoad[];
}): JSX.Element {
  const t = useT();
  if (data.rows.length === 0) {
    return <p className="text-sm text-nav-slate">{t('adminNav.noNavigators')}</p>;
  }
  const drillRow = data.rows.find((r) => String(r.navigatorId) === drill) ?? null;
  const drillCases = drill ? excItems.filter((i) => String(i.navigatorId) === drill) : [];

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto rounded-xl border-[1.5px] border-nav-card bg-white">
        <table className="w-full text-sm">
          <thead className="bg-nav-softer text-nav-slate">
            <tr>
              <Th>{t('adminNav.col.navigator')}</Th>
              <Th right>{t('f5r4a.nav.col.open')}</Th>
              {/* Conservas (E-23): las columnas vivas que el dibujo no mata. */}
              <Th right>{t('adminNav.col.cases')}</Th>
              <Th right>{t('adminNav.col.attempts')}</Th>
              <Th right>{t('adminNav.col.sessions')}</Th>
              <Th right>{t('adminNav.col.units')}</Th>
              <Th right>{t('f5r4a.nav.col.stale')}</Th>
              <Th right>{t('f5r4a.nav.col.due')}</Th>
              <Th right>{t('f5r4a.nav.col.gate')}</Th>
              <Th right>{t('adminNav.col.delta')}</Th>
              <Th right>{t('adminNav.col.checklist')}</Th>
              <Th right>{t('adminNav.col.pending')}</Th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => {
              const id = String(r.navigatorId);
              const on = drill === id;
              const gate = gateBy.get(id) ?? 0;
              const scope = scopeBy.get(id);
              return (
                <tr
                  key={r.navigatorId}
                  tabIndex={0}
                  aria-expanded={on}
                  aria-label={t('f5r4a.nav.rowAria', { name: r.navigatorName })}
                  onClick={() => setDrill(on ? null : id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDrill(on ? null : id); }
                  }}
                  className={cn(
                    'border-t border-nav-hair cursor-pointer transition-colors duration-150 motion-reduce:transition-none',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-nav-teal',
                    on ? 'bg-nav-tealTint hover:bg-[#d3e8e9]' : 'hover:bg-nav-tealTint/40',
                  )}
                >
                  <td className="px-3 py-2 pl-5">
                    <span className="flex items-center gap-[11px]">
                      <span className="flex-none inline-flex items-center justify-center w-[34px] h-[34px] rounded-full bg-nav-navy text-white font-head text-[12px] font-bold">
                        {initialsOf(r.navigatorName)}
                      </span>
                      <span className="min-w-0">
                        <span className="block font-head text-[13.5px] font-bold text-nav-navy">{r.navigatorName}</span>
                        {scope && (
                          <span className="block text-[12px] text-nav-slate">
                            {scope === 'closer' ? t('f5r4a.nav.scope.closer') : t('f5r4a.nav.scope.full')}
                          </span>
                        )}
                      </span>
                    </span>
                  </td>
                  <Td>{r.openCases}</Td>
                  <Td>{r.casesWorked}</Td>
                  <Td>{r.attempts}</Td>
                  <Td>{r.sessions}</Td>
                  <Td>{r.units}</Td>
                  {/* NECESITA-SERVER: sin tocar ≥14 d por navigator. */}
                  <td className="px-3 py-2 text-right text-nav-faint" title={t('f5r4a.nav.staleNa')}>—</td>
                  <td className="px-3 py-2 text-right"><ChipCount n={overdueBy.get(id) ?? 0} /></td>
                  <td className="px-3 py-2 text-right"><ChipCount n={gate} red /></td>
                  <td className="px-3 py-2 text-right">
                    {r.deltaMinutes === null ? (
                      <span className="text-nav-faint" title={t('adminNav.deltaNone')}>—</span>
                    ) : (
                      <span
                        className={cn(
                          Math.abs(r.deltaMinutes) >= 15 ? 'text-nav-amberText font-medium' : 'text-nav-navy',
                        )}
                        title={t('adminNav.deltaHint', { n: r.timedSessions })}
                      >
                        {r.deltaMinutes > 0 ? '+' : ''}{r.deltaMinutes} min
                      </span>
                    )}
                  </td>
                  <Td>{r.checklistAvg}/{data.checklistTotal}</Td>
                  <Td>{r.pendingMirror}</Td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Drill-down inline (dibujo: «para poder comparar personas sin perder
            la comparación»). Data viva: los casos con excepciones del
            navigator, con toda la maquinaria admin de cada caso. */}
        {drillRow && (
          <div className="bg-nav-softer border-t border-nav-hair px-5 py-[18px] animate-fade-up motion-reduce:animate-none">
            <div className="flex items-center justify-between gap-3.5 flex-wrap mb-3.5">
              <p className="font-head text-[14px] font-bold text-nav-navy">
                {t('f5r4a.nav.drillTitle', { name: drillRow.navigatorName })}
              </p>
              <Button variant="secondary" size="sm" className="min-h-[44px]" onClick={() => setDrill(null)}>
                {t('f5r4a.nav.drillClose')}
              </Button>
            </div>
            <p className="text-[11px] text-nav-slate mb-3">{t('f5r4a.nav.drillNote')}</p>
            {drillCases.length === 0 ? (
              <p className="text-sm text-nav-slate">
                {t('f5r4a.nav.drillEmpty', { name: drillRow.navigatorName })}
              </p>
            ) : (
              <ul className="rounded-[10px] border-[1.5px] border-nav-card bg-white divide-y divide-nav-hair">
                {drillCases.map((row) => (
                  <ExceptionRow key={row.caseId} row={row} loads={loads} />
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      <p className="text-[11px] text-nav-slate">
        {t('adminNav.deltaFooter')}
      </p>
    </div>
  );
}

const CASES_PAGE = 25;

/**
 * F5-R4a — Casos: píldoras de filtro con conteo arriba, filas (no tabla) y el
 * pie con «Página x de y · 25 por página» + Exportar CSV auditado. Píldoras
 * vivas: Prioritarios (somos) y Sin consentimiento (closedReason). Las cinco
 * compuestas del dibujo (Abiertos/Positivos/Negativos/En el buzón/Cerrados)
 * NECESITA-SERVER como filtro — quedan deshabilitadas con diagnóstico y con
 * el conteo vivo donde existe. Los selectores finos de siempre se conservan
 * (E-23): son los que sí filtran todo.
 */
function CasesTab(): JSX.Element {
  const t = useT();
  const qc = useQueryClient();
  const [f, setF] = useState<AdminNavFilters>({ status: 'all', findhelpHistory: 'all', limit: CASES_PAGE });
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ADMIN_NAV_KEYS.list(f),
    queryFn: () => adminNavApi.list(f),
  });
  // Conteos vivos para las píldoras SIN pasar por list() (que audita PHI):
  const summaryQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.summary,
    queryFn: () => adminNavApi.summary(),
    refetchInterval: POLL_MS,
  });
  const poolQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.bulkPool('in_pool'),
    queryFn: () => adminNavApi.bulkReassignPoolCount('in_pool'),
    refetchInterval: POLL_MS,
  });
  const somosQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.bulkPool('somos'),
    queryFn: () => adminNavApi.bulkReassignPoolCount('somos'),
    refetchInterval: POLL_MS,
  });

  // GC-3 #3: baja el CSV con LOS MISMOS filtros que la tabla muestra.
  const accessToken = useAuthStore((s) => s.accessToken);
  const [exporting, setExporting] = useState(false);
  const exportCsv = async (): Promise<void> => {
    if (!accessToken) return;
    setExporting(true);
    try {
      await downloadAdminNavCsv(f, accessToken);
    } catch (err) {
      toast.error(t('adminNav.export.failed'), (err as Error).message);
    } finally {
      setExporting(false);
    }
  };

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['admin', 'navigation'] });
  };

  const override = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) => adminNavApi.override(v.id, v.body),
    onSuccess: () => { toast.success(t('adminNav.overrideDone')); invalidate(); },
    onError: (err) => toast.error(t('adminNav.overrideFailed'), navError(err).message ?? ''),
  });

  const set = (patch: Partial<AdminNavFilters>): void => setF((s) => ({ ...s, ...patch, offset: 0 }));

  const somosOn = Boolean(f.somos);
  const sinconsentOn = f.closedReason === 'no_consent_ea';
  const counts = q.data?.counts;
  const openCount = counts
    ? (counts.suggested ?? 0) + (counts.flagged ?? 0) + (counts.in_progress ?? 0)
    : null;
  const closedCount = counts
    ? (counts.completed ?? 0) + (counts.declined_by_patient ?? 0) + (counts.not_required ?? 0) + (counts.closed_external ?? 0)
    : null;
  const noConsentCount = summaryQ.data?.byClosedReason?.no_consent_ea ?? null;

  const pills: Array<{
    key: string; label: string; count: number | null;
    live: boolean; on?: boolean; pick?: () => void;
  }> = [
    { key: 'abiertos', label: t('f5r4a.cases.pill.abiertos'), count: openCount, live: false },
    { key: 'positivos', label: t('f5r4a.cases.pill.positivos'), count: null, live: false },
    { key: 'negativos', label: t('f5r4a.cases.pill.negativos'), count: null, live: false },
    {
      key: 'prioritarios', label: t('f5r4a.cases.pill.prioritarios'),
      count: somosQ.data?.poolCount ?? null, live: true, on: somosOn,
      pick: () => set({ somos: !somosOn }),
    },
    { key: 'buzon', label: t('f5r4a.cases.pill.buzon'), count: poolQ.data?.poolCount ?? null, live: false },
    { key: 'cerrados', label: t('f5r4a.cases.pill.cerrados'), count: closedCount, live: false },
    {
      key: 'sinconsent', label: t('f5r4a.cases.pill.sinconsent'),
      count: noConsentCount, live: true, on: sinconsentOn,
      pick: () => set({ closedReason: sinconsentOn ? 'all' : 'no_consent_ea' }),
    },
  ];

  const page = Math.floor((f.offset ?? 0) / (f.limit ?? CASES_PAGE));
  const pages = q.data ? Math.max(1, Math.ceil(q.data.total / (f.limit ?? CASES_PAGE))) : 1;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="flex gap-2 flex-wrap">
          {pills.map((p) => (
            <button
              key={p.key}
              type="button"
              disabled={!p.live}
              aria-pressed={p.live ? Boolean(p.on) : undefined}
              aria-disabled={!p.live || undefined}
              title={p.live ? undefined : t('f5r4a.cases.pillNa')}
              onClick={p.pick}
              className={cn(
                'inline-flex items-center gap-2 min-h-[44px] px-[15px] rounded-lg border-[1.5px] font-head text-[12.5px] font-bold transition-colors duration-150 motion-reduce:transition-none',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
                !p.live
                  ? 'bg-white border-nav-card text-nav-faint cursor-not-allowed'
                  : p.on
                    ? 'bg-nav-navy border-nav-navy text-white hover:bg-nav-navyHover'
                    : 'bg-white border-nav-line text-nav-navy hover:border-nav-teal hover:text-nav-tealDark hover:bg-nav-tealTint/40',
              )}
            >
              {p.label}
              {p.count !== null && (
                <span className={cn(
                  'tabular-nums font-semibold',
                  p.live && p.on ? 'text-white/75' : 'text-nav-faint',
                )}>
                  {p.count}
                </span>
              )}
            </button>
          ))}
        </div>
        {/* NECESITA-SERVER: filtros compuestos del dibujo. */}
        <p className="text-[11px] text-nav-slate mt-1.5">{t('f5r4a.cases.pillsNaNote')}</p>
      </div>

      {/* Conservas (E-23): los selectores finos que sí filtran todo. */}
      <div className="flex gap-3 flex-wrap items-end">
        <div>
          <Label htmlFor="anv-status">{t('adminNav.filter.status')}</Label>
          <Select id="anv-status" value={f.status} className="max-w-[12rem]"
                  onChange={(e) => set({ status: e.target.value })}>
            <option value="all">{t('adminNav.filter.any')}</option>
            {/* D-15 — closed_external existe desde la 056 (se mudó / perdió la
                cobertura / inubicable / falleció) y faltaba en este filtro: el
                admin no podía listar los cierres por causa externa. */}
            {(['suggested', 'flagged', 'in_progress', 'completed', 'declined_by_patient', 'not_required', 'closed_external'] as NavStatus[])
              .map((s) => <option key={s} value={s}>{t(STATUS_KEYS[s])}</option>)}
          </Select>
        </div>

        <div>
          <Label htmlFor="anv-fh">{t('adminNav.filter.findhelp')}</Label>
          <Select id="anv-fh" value={f.findhelpHistory} className="max-w-[14rem]"
                  onChange={(e) => set({ findhelpHistory: e.target.value })}>
            <option value="all">{t('adminNav.filter.any')}</option>
            <option value="none">{t('adminNav.fh.none')}</option>
            <option value="incomplete">{t('adminNav.fh.incomplete')}</option>
            <option value="complete">{t('adminNav.fh.complete')}</option>
          </Select>
        </div>

        {/* GC-3 #3 (CEO): poder listar por CÓMO cerró — el no-consentimiento
            es data enrichment, no basura. El catálogo es el mismo del cierre. */}
        <div>
          <Label htmlFor="anv-reason">{t('adminNav.filter.closedReason')}</Label>
          <Select id="anv-reason" value={f.closedReason ?? 'all'} className="max-w-[14rem]"
                  onChange={(e) => set({ closedReason: e.target.value })}>
            <option value="all">{t('adminNav.filter.any')}</option>
            {(Object.keys(CLOSED_REASON_KEYS) as ClosedReasonCode[]).map((code) => (
              <option key={code} value={code}>{t(CLOSED_REASON_KEYS[code])}</option>
            ))}
          </Select>
        </div>

        <div>
          <Label htmlFor="anv-search">{t('adminNav.filter.search')}</Label>
          <div className="flex gap-2">
            <Input id="anv-search" value={search} placeholder="a1b2c3"
                   onChange={(e) => setSearch(e.target.value)} className="max-w-[12rem]" />
            <Button variant="secondary" size="sm" onClick={() => set({ search: search.trim() || undefined })}>
              {t('adminNav.filter.apply')}
            </Button>
          </div>
        </div>

        <div className="flex gap-3 pb-1">
          <Checkbox checked={Boolean(f.urgent)} onChange={(e) => set({ urgent: e.target.checked })}
                    label={t('adminNav.filter.urgent')} />
          <Checkbox checked={Boolean(f.somos)} onChange={(e) => set({ somos: e.target.checked })}
                    label={t('adminNav.filter.somos')} />
          <Checkbox checked={Boolean(f.overdue)} onChange={(e) => set({ overdue: e.target.checked })}
                    label={t('adminNav.filter.overdue')} />
        </div>
      </div>

      {q.isLoading && <Loading />}
      {q.isError && <LoadError />}

      {q.data && (
        <>
          <p className="text-sm text-nav-slate">
            {t('adminNav.showing', { n: q.data.items.length, total: q.data.total })}
          </p>
          <div className="rounded-xl border-[1.5px] border-nav-card bg-white overflow-hidden">
            <ul className="divide-y divide-nav-hair">
              {q.data.items.map((c) => {
                const open = openId === c.id;
                return (
                  <li key={c.id}>
                    <div className="flex items-center gap-3.5 flex-wrap min-h-[66px] px-5 py-[11px]">
                      <span className="flex-1 min-w-[200px]">
                        <span className="block font-head text-[13.5px] font-bold text-nav-navy">
                          {c.clientLastName || '—'}{' '}
                          <span className="font-mono text-xs font-normal text-nav-slate">…{c.cinHashSuffix}</span>
                          {c.somosPriority && (
                            <span className="ml-1.5 text-[11px] font-body font-normal px-1.5 py-0.5 rounded-full bg-nav-amberTint text-nav-amberText">
                              SOMOS
                            </span>
                          )}
                        </span>
                        <span className="block mt-0.5 text-[12.5px] text-nav-slate tabular-nums">
                          {(c.claimNavigatorName ?? t('admin.nav.unassigned'))}
                          {' · '}{t('f5r4a.cases.attempts', { n: c.outreachAttempts })}
                          {c.slaDeadline ? ` · SLA ${c.slaDeadline}` : ''}
                          {' · '}{t(fhLabel(c.findhelpHistory))}
                        </span>
                      </span>
                      <span className={cn(
                        'flex-none rounded-full px-[11px] py-1 border font-head text-[11.5px] font-bold whitespace-nowrap',
                        c.status === 'suggested' || c.status === 'flagged' || c.status === 'in_progress'
                          ? 'bg-nav-tealTint text-nav-tealDark border-[#c9e2e2]'
                          : 'bg-nav-hair text-nav-slate border-nav-card',
                      )}>
                        {t(STATUS_KEYS[c.status])}
                      </span>
                      {c.priority === 'urgent' && (
                        <span className="flex-none text-[11px] px-1.5 py-0.5 rounded-full bg-nav-redTint text-nav-red">
                          {t('navigator.badge.urgent')}
                        </span>
                      )}
                      <Button variant="secondary" size="sm" className="min-h-[44px]" onClick={() => setOpenId(open ? null : c.id)}>
                        {open ? t('f5r4a.cases.close') : t('f5r4a.cases.open')}
                      </Button>
                    </div>
                    {open && (
                      <div className="px-5 pb-4 flex flex-col gap-3 bg-nav-softer/60 pt-3 border-t border-nav-hair">
                        {/* Conservas (E-23): las dos palancas vivas por caso. */}
                        <div className="flex gap-1.5 flex-wrap">
                          {c.claimNavigatorId && (
                            <Button variant="ghost" size="sm"
                                    onClick={() => override.mutate({ id: c.id, body: { releaseClaim: true } })}>
                              {t('adminNav.action.release')}
                            </Button>
                          )}
                          {c.priority !== 'urgent' && (
                            <Button variant="ghost" size="sm"
                                    onClick={() => override.mutate({ id: c.id, body: { priority: 'urgent' } })}>
                              {t('adminNav.action.urgent')}
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                          <CaseForms caseId={c.id} />
                          <TracksPanel caseId={c.id} />
                        </div>
                        <AttachmentsPanel caseId={c.id} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="flex items-center justify-between gap-3.5 flex-wrap px-5 py-3.5 border-t border-nav-hair bg-nav-softer">
              <p className="text-[13px] text-nav-slate tabular-nums">
                {t('f5r4a.cases.pageFoot', { page: page + 1, pages })}
              </p>
              {/* Sin size="sm" a propósito: el pie del dibujo pide 44px y la
                  custodia C4d de T2 vigila que el pager no conviva con el
                  botón chico del inline muerto de SCALE. */}
              <Button
                variant="secondary"
                className="min-h-[44px]"
                disabled={exporting}
                onClick={() => void exportCsv()}
              >
                {exporting ? t('adminNav.export.working') : t('f5r4a.cases.export')}
              </Button>
            </div>
          </div>
          {/* F5-T2: pager del chasis único (era el inline de SCALE). */}
          <ChassisPager
            page={page}
            pages={pages}
            busy={q.isFetching}
            onPage={(n) => {
              setOpenId(null);
              setF((s) => ({ ...s, offset: n * (s.limit ?? CASES_PAGE) }));
            }}
            prevLabel={t('adminNav.pager.prev')}
            nextLabel={t('adminNav.pager.next')}
            pageLabel={t('adminNav.pager.page', { page: page + 1, pages })}
          />
        </>
      )}
    </div>
  );
}


/**
 * Priority-list import (SOMOS / payer lists). Upload → dry-run → apply.
 * Navigator UI stays unlabeled; only queue ORDER BY somos_priority changes.
 *
 * F5-R4a: dos tarjetas del dibujo — «1 · El archivo del pagador» (dropzone)
 * y «2 · Preview · 3 · Aplicar» (tres números + caja ámbar honesta). El gate
 * no cambia: Aplicar sigue muerto hasta correr el preview (E-18).
 */
function PriorityListTab(): JSX.Element {
  const t = useT();
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin';
  const [file, setFile] = useState<File | null>(null);
  const [createMissing, setCreateMissing] = useState(true);
  const [navigatorId, setNavigatorId] = useState('');
  const [preview, setPreview] = useState<PriorityListPreview | null>(null);
  const [lastApply, setLastApply] = useState<PriorityListApplyResult | null>(null);

  const loads = useQuery({
    queryKey: ADMIN_NAV_KEYS.loads,
    queryFn: () => adminNavApi.assignmentLoads(),
  });

  const previewMut = useMutation({
    mutationFn: (f: File) => adminNavApi.priorityListPreview(f),
    onSuccess: (r) => {
      setPreview(r);
      setLastApply(null);
      toast.success(t('adminNav.priorityList.previewDone'));
    },
    onError: (e) => toast.error(t('adminNav.priorityList.failed'), navError(e).message ?? ''),
  });

  const applyMut = useMutation({
    mutationFn: (f: File) =>
      adminNavApi.priorityListApply(f, {
        createMissingCases: createMissing,
        navigatorId: navigatorId || undefined,
      }),
    onSuccess: (r) => {
      setLastApply(r);
      setPreview(r);
      toast.success(t('adminNav.priorityList.applyDone', {
        priority: r.prioritySet,
        created: r.casesCreated,
        reassigned: r.reassigned,
      }));
    },
    onError: (e) => toast.error(t('adminNav.priorityList.failed'), navError(e).message ?? ''),
  });

  const busy = previewMut.isPending || applyMut.isPending;
  const report = lastApply ?? preview;

  const fmtSize = (bytes: number): string =>
    bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

  // «Se crearían»: con la casilla prendida, cada matched sin caso abre episodio
  // y cada not-found crea cliente + caso (E-18). Con la casilla apagada, nada.
  const wouldCreate = report ? (createMissing ? report.eligibleForCreate + report.notFound : 0) : 0;
  const invalidRows = report ? Math.max(0, report.totalRows - report.uniqueCins) : 0;
  const destNavName = (loads.data ?? []).find((n) => String(n.navigatorId) === String(navigatorId))?.navigatorName;

  return (
    <div className="flex flex-col gap-4">
      {!isAdmin && (
        <Alert>
          <AlertDescription>{t('adminNav.priorityList.adminOnly')}</AlertDescription>
        </Alert>
      )}
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <section className="rounded-xl border-[1.5px] border-nav-card bg-white p-[22px]">
          <p className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal mb-3.5">
            {t('f5r4a.prio.k1')}
          </p>
          <label
            htmlFor="priority-list-file"
            className="flex flex-col items-center justify-center min-h-[140px] bg-nav-tealTint/40 border-[1.5px] border-dashed border-nav-teal rounded-xl p-[18px] text-center cursor-pointer focus-within:ring-2 focus-within:ring-nav-teal"
          >
            <Upload className="h-6 w-6 text-nav-teal" aria-hidden />
            {file ? (
              <>
                <p className="mt-3 mb-1 font-head text-[14px] font-bold text-nav-navy break-all">{file.name}</p>
                <p className="text-[12.5px] text-nav-slate tabular-nums">
                  {report && report.filename === file.name
                    ? t('f5r4a.prio.fileRows', { rows: report.totalRows, size: fmtSize(file.size) })
                    : fmtSize(file.size)}
                </p>
              </>
            ) : (
              <>
                <p className="mt-3 mb-1 font-head text-[14px] font-bold text-nav-navy">{t('f5r4a.prio.dropCta')}</p>
                <p className="text-[12.5px] text-nav-slate">{t('f5r4a.prio.noFile')}</p>
              </>
            )}
            <Input
              id="priority-list-file"
              type="file"
              accept=".xlsx,.xls,.csv,.txt"
              className="sr-only"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setPreview(null);
                setLastApply(null);
              }}
            />
          </label>
          <p className="mt-3.5 text-[12.5px] leading-relaxed text-nav-slate">{t('f5r4a.prio.dropHint')}</p>

          {/* Conservas (E-23): la casilla de crear casos y el navigator opcional
              del apply-v2 — el dibujo ni los dibuja ni los mata. */}
          <div className="mt-4 flex flex-col gap-3">
            <Checkbox
              checked={createMissing}
              onChange={(e) => { setCreateMissing(e.target.checked); }}
              label={t('adminNav.priorityList.createCases')}
              disabled={!isAdmin}
            />
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="priority-nav">{t('adminNav.priorityList.navigator')}</Label>
              <Select
                id="priority-nav"
                value={navigatorId}
                onChange={(e) => setNavigatorId(e.target.value)}
                disabled={!isAdmin || loads.isLoading}
              >
                <option value="">{t('adminNav.priorityList.navigatorNone')}</option>
                {(loads.data ?? []).map((n) => (
                  <option key={n.navigatorId} value={n.navigatorId}>{n.navigatorName}</option>
                ))}
              </Select>
            </div>
          </div>
        </section>

        <section className="rounded-xl border-[1.5px] border-nav-card bg-white p-[22px]">
          <p className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal mb-3.5">
            {t('f5r4a.prio.k2')}
          </p>

          {report ? (
            <>
              <div className="grid gap-3.5 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}>
                <span>
                  <span className="block font-head text-[30px] font-extrabold leading-none tracking-[-0.02em] tabular-nums text-nav-navy">
                    {report.matched}
                  </span>
                  <span className="block mt-1.5 text-[12.5px] text-nav-slate">{t('f5r4a.prio.stat.match')}</span>
                </span>
                <span>
                  <span className="block font-head text-[30px] font-extrabold leading-none tracking-[-0.02em] tabular-nums text-nav-amberText">
                    {wouldCreate}
                  </span>
                  <span className="block mt-1.5 text-[12.5px] text-nav-amberText">{t('f5r4a.prio.stat.create')}</span>
                </span>
                <span title={t('f5r4a.prio.invalidHint')}>
                  <span className="block font-head text-[30px] font-extrabold leading-none tracking-[-0.02em] tabular-nums text-nav-faint">
                    {invalidRows}
                  </span>
                  <span className="block mt-1.5 text-[12.5px] text-nav-slate">{t('f5r4a.prio.stat.invalid')}</span>
                </span>
              </div>

              <div className="rounded-xl border-[1.5px] border-nav-amber bg-nav-amberTint px-[18px] py-4">
                <p className="text-[13px] leading-relaxed text-nav-amberText">
                  {!createMissing
                    ? t('f5r4a.prio.amberNoCreate')
                    : navigatorId && destNavName
                      ? t('f5r4a.prio.amberNav', { c: wouldCreate, name: destNavName })
                      : t('f5r4a.prio.amberPool', { c: wouldCreate })}
                </p>
              </div>

              {/* Detalle vivo del preview (conserva): los seis números finos. */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm mt-4">
                <PriorityStat label={t('adminNav.priorityList.stat.unique')} value={report.uniqueCins} />
                <PriorityStat label={t('adminNav.priorityList.stat.matched')} value={report.matched} />
                <PriorityStat label={t('adminNav.priorityList.stat.notFound')} value={report.notFound} />
                <PriorityStat label={t('adminNav.priorityList.stat.open')} value={report.openCase} />
                <PriorityStat label={t('adminNav.priorityList.stat.eligible')} value={report.eligibleForCreate} />
                <PriorityStat label={t('adminNav.priorityList.stat.already')} value={report.alreadyPriority} />
              </div>
              {lastApply && (
                <p className="mt-3 text-sm text-nav-navy">
                  {t('adminNav.priorityList.applyDone', {
                    priority: lastApply.prioritySet,
                    created: lastApply.casesCreated,
                    reassigned: lastApply.reassigned,
                  })}
                </p>
              )}
              {report.notFoundSample.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-nav-slate mb-1">
                    {t('adminNav.priorityList.notFoundSample')}
                  </p>
                  <p className="text-xs font-mono text-nav-slate break-all">
                    {report.notFoundSample.join(', ')}
                  </p>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-nav-slate">{t('adminNav.priorityList.hint')}</p>
          )}

          <div className="flex justify-end gap-2.5 mt-4 flex-wrap">
            {report && (
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => { setPreview(null); setLastApply(null); }}
              >
                {t('f5r4a.prio.discard')}
              </Button>
            )}
            <Button
              variant="secondary"
              disabled={!file || busy}
              onClick={() => {
                if (!file) { toast.error(t('adminNav.priorityList.needFile')); return; }
                previewMut.mutate(file);
              }}
            >
              {previewMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t('adminNav.priorityList.preview')}
            </Button>
            <Button
              disabled={!isAdmin || !file || !preview || busy}
              onClick={() => {
                if (!file) { toast.error(t('adminNav.priorityList.needFile')); return; }
                if (!preview) { toast.error(t('adminNav.priorityList.needPreview')); return; }
                applyMut.mutate(file);
              }}
            >
              {applyMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t('f5r4a.prio.apply')}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}

function PriorityStat({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="rounded-[10px] bg-nav-softer px-3 py-2">
      <div className="text-xs text-nav-slate">{label}</div>
      <div className="font-head text-lg font-bold text-nav-navy tabular-nums">{value}</div>
    </div>
  );
}


/**
 * F5-T1a — la tercera vía (claves crudas en Admin 06) muere. Puente a Admin 08.
 * NAV_OPERATIONAL_SETTING_KEYS sigue exportado como inventario; no se edita acá.
 *
 * F5-R4a: revestida con la tarjeta del dibujo (borde ámbar + icono + título).
 * El destino vivo sigue siendo /admin/settings?group=navegacion; el testid y
 * la clave del bridge se conservan (los anclan custodias).
 */
function ConfigTab(): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-3" data-testid="f5-t1a-admin06-bridge">
      <div className="flex items-start gap-4 flex-wrap rounded-xl border-[1.5px] border-nav-amber bg-white p-[22px]">
        <span className="flex-none inline-flex items-center justify-center w-10 h-10 rounded-full bg-nav-amberTint text-nav-amberText">
          <AlertTriangle className="h-5 w-5" aria-hidden />
        </span>
        <div className="flex-1 min-w-[240px]">
          <p className="font-head text-[16px] font-bold text-nav-navy mb-2">{t('f5r4a.cfg.title')}</p>
          <p className="text-[14px] leading-relaxed text-nav-navy mb-1.5">{t('f5r4a.cfg.body')}</p>
          <p className="text-[12.5px] text-nav-slate mb-4">{t('f5t1a.admin06.bridge')}</p>
          <Link
            to="/admin/settings?group=navegacion"
            className="inline-flex items-center justify-center min-h-[44px] px-5 bg-nav-navy text-white border-[1.5px] border-nav-navy rounded-lg font-head text-[13.5px] font-bold hover:bg-nav-navyHover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
          >
            {t('f5t1a.admin06.cta')}
          </Link>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ bits

function Stat({ value, label, tone }: { value: number; label: string; tone: 'rose' | 'peach' | 'plain' }): JSX.Element {
  const tones: Record<string, string> = {
    rose: 'bg-nav-redTint border-[#e3b5ae] text-nav-red',
    peach: 'bg-nav-amberTint border-nav-amber/40 text-nav-amberText',
    plain: 'bg-white border-nav-card text-nav-navy',
  };
  return (
    <div className={cn('rounded-xl border-[1.5px] px-4 py-3 min-w-[8rem]', tones[tone])}>
      <p className="font-head text-[22px] font-bold leading-tight tabular-nums">{value}</p>
      <p className="font-head text-[10.5px] font-bold tracking-[0.08em] uppercase opacity-80 mt-0.5">{label}</p>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="rounded-xl border-[1.5px] border-nav-card bg-white p-4">
      <h2 className="font-head text-[13px] font-bold text-nav-navy mb-2">{title}</h2>
      {children}
    </section>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }): JSX.Element {
  return (
    <th className={cn('px-3 h-[38px] font-head text-[10.5px] font-bold tracking-[0.08em] uppercase whitespace-nowrap', right ? 'text-right' : 'text-left')}>
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }): JSX.Element {
  return <td className="px-3 py-2 text-right text-nav-navy font-head font-bold tabular-nums">{children}</td>;
}

function Loading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex items-center gap-2 text-sm text-nav-slate py-6">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      {t('adminNav.loading')}
    </div>
  );
}

function LoadError(): JSX.Element {
  const t = useT();
  return (
    <Alert variant="error" role="alert">
      <AlertDescription>{t('adminNav.loadFailed')}</AlertDescription>
    </Alert>
  );
}
